/**
 * @class ProductInformationTabs
 * @description Web Component for product information tabs with accessible keyboard navigation
 */
if (!customElements.get('product-information-tabs')) {
  class ProductInformationTabs extends HTMLElement {
    constructor() {
      super();
      this.tabs = [];
      this.panels = [];
      this.currentIndex = 0;
    }

    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

      if (this.tabs.length === 0 || this.panels.length === 0) return;

      this.setupEventListeners();
      this.setInitialState();
    }

    disconnectedCallback() {
      this.removeEventListeners();
    }

    setupEventListeners() {
      this.handleTabClick = this.handleTabClick.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.handleTabClick);
        tab.addEventListener('keydown', this.handleKeyDown);
      });

      // Support for Shopify Theme Editor
      if (Shopify.designMode) {
        this.handleBlockSelect = this.handleBlockSelect.bind(this);
        this.addEventListener('shopify:block:select', this.handleBlockSelect);
      }
    }

    removeEventListeners() {
      this.tabs.forEach((tab) => {
        tab.removeEventListener('click', this.handleTabClick);
        tab.removeEventListener('keydown', this.handleKeyDown);
      });

      if (Shopify.designMode) {
        this.removeEventListener('shopify:block:select', this.handleBlockSelect);
      }
    }

    setInitialState() {
      // Find the active tab or default to first
      const activeTabIndex = this.tabs.findIndex((tab) =>
        tab.classList.contains('product-info-tabs__tab--active')
      );
      this.currentIndex = activeTabIndex !== -1 ? activeTabIndex : 0;
    }

    handleTabClick(event) {
      const clickedTab = event.currentTarget;
      const index = this.tabs.indexOf(clickedTab);
      if (index !== -1) {
        this.selectTab(index);
      }
    }

    handleKeyDown(event) {
      let newIndex = this.currentIndex;

      switch (event.key) {
        case 'ArrowLeft':
          newIndex = this.currentIndex - 1;
          if (newIndex < 0) newIndex = this.tabs.length - 1;
          event.preventDefault();
          break;
        case 'ArrowRight':
          newIndex = this.currentIndex + 1;
          if (newIndex >= this.tabs.length) newIndex = 0;
          event.preventDefault();
          break;
        case 'Home':
          newIndex = 0;
          event.preventDefault();
          break;
        case 'End':
          newIndex = this.tabs.length - 1;
          event.preventDefault();
          break;
        case 'Enter':
        case ' ':
          this.selectTab(this.currentIndex);
          event.preventDefault();
          return;
        default:
          return;
      }

      this.selectTab(newIndex);
      this.tabs[newIndex].focus();
    }

    handleBlockSelect(event) {
      // Find the tab that corresponds to the selected block
      const tabName = event.target.closest('[data-panel]')?.dataset.panel;
      if (tabName) {
        const tabIndex = this.tabs.findIndex((tab) => tab.dataset.tab === tabName);
        if (tabIndex !== -1) {
          this.selectTab(tabIndex);
        }
      }
    }

    selectTab(index) {
      if (index === this.currentIndex && this.tabs[index].getAttribute('aria-selected') === 'true') {
        return;
      }

      // Deactivate all tabs
      this.tabs.forEach((tab, i) => {
        const isSelected = i === index;
        tab.setAttribute('aria-selected', isSelected.toString());
        tab.setAttribute('tabindex', isSelected ? '0' : '-1');
        tab.classList.toggle('product-info-tabs__tab--active', isSelected);
      });

      // Hide all panels and show the selected one
      this.panels.forEach((panel, i) => {
        const isActive = i === index;
        panel.classList.toggle('product-info-tabs__panel--active', isActive);
        if (isActive) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });

      this.currentIndex = index;
    }
  }

  customElements.define('product-information-tabs', ProductInformationTabs);
}
